import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import { cx, css } from 'emotion';
import ProcessUtils from 'utils/ProcessUtils';
import Constants from 'utils/Constants';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import ProcessDataTable from 'components/core/ProcessDataTable';
import RIEToggle from 'components/core/editable/RIEToggle';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import { colorTextGrey } from 'styles/base';

const overrideTableStyles = css`

`;

const coordinateSystemLabelStyles = css`
    color: ${colorTextGrey}
`;

export default class RetrievedVariableTransformation extends React.Component {
    static propTypes = {
        process: PropTypes.object,
        processErrors: PropTypes.object,
        retrievedVariableNames: PropTypes.array,
        coordinateSystemName: PropTypes.string,
        onOverrideDisplayChange: PropTypes.func,
        onOverrideChange: PropTypes.func,
        isOverrideTableVisible: PropTypes.bool,
        overrideTableData: PropTypes.object, // { [coordinateSystemName]: { [dimensionName]: transformationTypeOverrideValue } }
        overrideTableClassName: PropTypes.string,
        spreadsheetView: PropTypes.bool // specific display and setting specific to this being included in the spreadsheet edit view
    };

    static defaultProps = {
        spreadsheetView: false
    };

    shouldParameterBeDisplayed = () => {
        //only display if variable has coordinate system and dimensions
        let shouldDisplay = false;
        this.props.retrievedVariableNames.forEach(variableName => {
            const retrievedVariable = this.props.process.variable_retrieval.retrieved_variables[variableName];
            let coordinateSystemName = this.props.coordinateSystemName;
            if (this.props.coordinateSystemName === undefined) {
                // If we haven't defined a new coordinate system to apply to all variables in the list, then
                // use the original coordinate system for the variable
                coordinateSystemName = retrievedVariable.coordinate_system;
            }
            if (coordinateSystemName) {
                const transformableDims = [];
                if (retrievedVariable.dims) {
                    retrievedVariable.dims.forEach(dimensionName => {
                        const dimension = ProcessUtils.getDimension(this.props.process, coordinateSystemName, dimensionName);

                        if (dimension) { // Krista wants to be able to override even if the coord sys default is passthrough
                            transformableDims.push(dimensionName);
                        }

                    });
                }

                if (transformableDims.length > 0) {
                    shouldDisplay = true;
                }
            }
        });
        return shouldDisplay;
    };

    render = () => {
        const infoBoxText = '';
        const variableLabel = this.props.retrievedVariableNames.length > 1 ? 'these variables' : 'this variable';
        if (this.shouldParameterBeDisplayed()) {
            return (
                <Flexbox flexDirection={'column'} className="transformation-override">
                    {!this.props.spreadsheetView &&
                    <Flexbox flexDirection={'row'} alignItems="center" marginBottom="15px">
                        <InfoBoxLabel
                            label={`Do you want to override coordinate system transformation for ${variableLabel}?`}
                            hideColon
                            message={infoBoxText}
                        />
                        <Flexbox>
                            <RIEToggle
                                value={this.props.isOverrideTableVisible}
                                textFalse="No"
                                textTrue="Yes"
                                change={this.props.onOverrideDisplayChange}
                                radioGroupName="transformation-override"
                                formDisplay
                            />
                        </Flexbox>
                    </Flexbox>
                    }
                    {this.props.isOverrideTableVisible &&
                    <Flexbox className={cx('override-table', overrideTableStyles)}>
                        <OverrideTransformationTable
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            retrievedVariableNames={this.props.retrievedVariableNames}
                            onOverrideChange={this.props.onOverrideChange}
                            tableRowData={this.props.overrideTableData}
                            selectable={false}
                            spreadsheetView={this.props.spreadsheetView}
                            className={this.props.overrideTableClassName}
                        />
                    </Flexbox>
                    }
                </Flexbox>
            );
        } else if (this.props.spreadsheetView) {
            const emptyStyles = css`
                color: ${colorTextGrey};
                text-align: center;
                font-style: italic;
                padding: 5px 0;
                width: 100%;
                display: inline-block;
            `;
            return (<span className={emptyStyles}>No applicable advanced settings.</span>);
        } else {
            return <div/>;
        }

    };
}

class OverrideTransformationTable extends ProcessDataTable {
    static propTypes = {
        retrievedVariableNames: PropTypes.array.isRequired,
        onOverrideChange: PropTypes.func.isRequired,
        tableRowData: PropTypes.object.isRequired, // { [coordinateSystemName]: { [dimensionName]: transformationTypeOverrideValue } }
        spreadsheetView: PropTypes.bool
    };

    _initializePropDependentVariables(props) {
        super._initializePropDependentVariables(props);
        //if there are multiple coordinate systems in list, we need to display names in table to distinguish
        //for multi edit modal
        this.displayCoordinateSystemName = Object.keys(props.tableRowData).length > 1;
    };

    getTableRowData = (props) => {
        const rows = [];
        Object.keys(props.tableRowData).forEach(coordinateSystemName => {
            const rowData = props.tableRowData[coordinateSystemName];
            Object.keys(rowData).forEach(dimensionName => {
                const dimension = ProcessUtils.getDimension(props.process, coordinateSystemName, dimensionName);
                if (dimension) {
                    const defaultTransformationType = dimension.data_transformation.transformation_type;
                    const overrideValue = rowData[dimensionName];

                    rows.push({
                        key: `${coordinateSystemName}-${dimensionName}`,
                        dimensionData: { dimensionName, coordinateSystemName, overrideValue },
                        defaultTransformationType
                    });
                }
            });
        });

        return rows;
    };

    getRowKey() {
        return 'key';
    }

    renderDimension = (dimensionData) => {
        return (
            <div>
                {this.displayCoordinateSystemName &&
                    <span className={coordinateSystemLabelStyles}>{dimensionData.coordinateSystemName}: </span>
                }
                <span>{dimensionData.dimensionName}</span>
            </div>
        );
    };

    renderDefault = (defaultTransformationType) => {
        return Constants.getOptionById(defaultTransformationType, Constants.COORDINATE_SYSTEM_TRANSFORM_TYPES).label;
    };

    renderOverride = (dimensionData) => {
        const onChangeData = dimensionData;
        if (this.props.spreadsheetView) {
            // in spreadsheet edit view, there is only one variable for the table
            onChangeData.retrievedVariableName = this.props.retrievedVariableNames[0];
        }

        return (
            <RIEComboBox
                value={Constants.getOptionById(dimensionData.overrideValue, Constants.VARIABLE_OVERRIDE_TRANSFORM_TYPES)}
                options={Constants.VARIABLE_OVERRIDE_TRANSFORM_TYPES}
                change={this.props.onOverrideChange}
                onChangeData={onChangeData}
                clearable
            />
        );
    };

    getTableColumns() {
        const dimensionColumn = {
            property: 'dimensionData',
            header: {
                label: 'Dimension',
                props: {
                    style: {
                        whiteSpace: 'nowrap',
                    }
                }
            },
            cell: {
                formatters: [
                    dimensionData => { return this.renderDimension(dimensionData); }
                ]
            }
        };
        const defaultColumn = {
            property: 'defaultTransformationType',
            header: {
                label: 'Coordinate System Default',
                props: {
                    style: {
                        whiteSpace: 'nowrap'
                    }
                }
            },
            cell: {
                formatters: [
                    defaultTransformationType => { return this.renderDefault(defaultTransformationType); }
                ]
            }
        };

        const overrideColumn = {
            property: 'dimensionData',
            header: {
                label: 'Variable Transformation Method',
                props: {
                    style: {
                        whiteSpace: 'nowrap'
                    }
                }
            },
            cell: {
                formatters: [
                    dimensionData => { return this.renderOverride(dimensionData); }
                ]
            }
        };

        return [dimensionColumn, defaultColumn, overrideColumn];
    }
}



// WEBPACK FOOTER //
// ./src/components/core/fields/transformation_parameters/RetrievedVariableTransformation.js